import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { ArrowUpRight, CheckCircle2, HardHat, Gauge, Layers } from "lucide-react";
import aboutImg from "@/assets/about-drill.jpg";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

function Reveal({ children, delay = 0, direction = "up", className = "" }: { children: React.ReactNode; delay?: number; direction?: "up" | "left" | "right"; className?: string }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-8%" });
    const hidden = direction === "up" ? { opacity: 0, y: 40 } : direction === "left" ? { opacity: 0, x: 50 } : { opacity: 0, x: -50 };
    const visible = direction === "up" ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 };
    return (
        <motion.div ref={ref} className={className} initial="hidden" animate={inView ? "visible" : "hidden"}
            variants={{ hidden, visible: { ...visible, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } } }}>
            {children}
        </motion.div>
    );
}

function StaggerGrid({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-8%" });
    return (
        <motion.div ref={ref} className={className} initial="hidden" animate={inView ? "visible" : "hidden"}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.3, delayChildren: 0.1 } } }}>
            {children}
        </motion.div>
    );
}

function StaggerItem({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return (
        <motion.div className={className} variants={{ hidden: { opacity: 0, y: 35 }, visible: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } } }}>
            {children}
        </motion.div>
    );
}

export default function Equipements() {
    const { t } = useTranslation("equipements");
    const rcMachines = t("categories.rc.machines", { returnObjects: true }) as { name: string; type: string; detail: string; badge: string }[];
    const ugMachines = t("categories.ug.machines", { returnObjects: true }) as { name: string; type: string; detail: string; badge: string }[];
    const ddHighlights = t("categories.dd.highlights", { returnObjects: true }) as { title: string; desc: string }[];

    return (
        <>
            <Helmet>
                <title>{t("meta_title")}</title>
                <meta name="description" content={t("meta_desc")} />
                <meta name="keywords" content={t("meta_keywords")} />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content={t("meta_title")} />
                <meta property="og:description" content={t("meta_desc")} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://salmamydrill.com/equipements" />
                <meta property="og:image" content={aboutImg} />
                <meta property="og:locale" content="fr_FR" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={t("meta_title")} />
                <meta name="twitter:description" content={t("meta_desc")} />
                <meta name="twitter:image" content={aboutImg} />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Inter:wght@400;500;600&display=swap" />
            </Helmet>
            <div className="min-h-screen bg-background text-foreground flex flex-col overflow-x-hidden">
                <Nav />

                {/* Hero */}
                <section className="relative bg-surface text-surface-foreground min-h-[60vh] flex items-end overflow-hidden grain">
                    <motion.div initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 0.3, scale: 1 }} transition={{ duration: 2.2, ease: "easeOut" }} className="absolute inset-0 pointer-events-none mix-blend-screen">
                        <img src={aboutImg} alt="Flotte de foreuses Salmamy Drill" className="w-full h-full object-cover grayscale" />
                    </motion.div>
                    <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/80 to-transparent" />

                    <div className="relative z-10 mx-auto max-w-7xl px-6 pt-32 pb-20 md:pt-48 md:pb-24 w-full flex flex-col md:flex-row md:items-end justify-between gap-10">
                        <div>
                            <motion.span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-8" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}>
                                <span className="w-12 h-px bg-primary/40 block"></span>
                                {t("hero.eyebrow")}
                            </motion.span>
                            <div className="overflow-hidden mb-4">
                                <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                                    <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-display font-black leading-[0.9] tracking-tighter uppercase text-surface-foreground">
                                        {t("hero.h1_line1")}<br />
                                        <span className="text-surface-foreground/30 italic">{t("hero.h1_accent")}</span>
                                    </h1>
                                </motion.div>
                            </div>
                        </div>
                        <motion.div className="bg-surface-foreground text-surface p-10 hidden md:block" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}>
                            <div className="font-display text-7xl font-black leading-none flex items-baseline">{t("hero.stat_n")}<span className="text-primary text-3xl ml-3 font-semibold tracking-widest uppercase">{t("hero.stat_unit")}</span></div>
                            <p className="mt-4 font-mono text-xs uppercase tracking-widest opacity-70">{t("hero.stat_sub")}</p>
                        </motion.div>
                    </div>
                </section>

                {/* Intro */}
                <section className="py-16 md:py-24 px-6 border-b border-border">
                    <div className="mx-auto max-w-7xl">
                        <Reveal direction="up">
                            <div className="max-w-3xl text-xl font-medium text-foreground/80 leading-relaxed border-l-2 border-primary pl-8 py-2">
                                {t("intro")}
                            </div>
                        </Reveal>
                    </div>
                </section>

                {/* Equipment Categories */}
                <section className="py-20 md:py-32 px-6">
                    <div className="mx-auto max-w-7xl space-y-20 md:space-y-32">

                        {/* Surface RC */}
                        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-start">
                            <Reveal direction="left" className="lg:sticky top-32">
                                <div className="inline-flex p-4 rounded-full bg-surface/5 text-primary mb-6">
                                    <Gauge strokeWidth={1.5} className="w-8 h-8" />
                                </div>
                                <h2 className="text-4xl font-display font-black uppercase tracking-tight mb-4 text-foreground">{t("categories.rc.title_line1")} <br /><span className="text-foreground/30 italic">{t("categories.rc.title_accent")}</span></h2>
                                <p className="text-foreground/60 leading-relaxed font-medium mb-6">
                                    {t("categories.rc.desc")}
                                </p>
                                <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-primary">{t("categories.rc.machine_count")}</div>
                            </Reveal>

                            <StaggerGrid className="grid gap-4">
                                {rcMachines.map((rig, i) => (
                                    <StaggerItem key={i}>
                                        <div className="group bg-surface/5 p-6 border border-border hover:border-primary/50 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div>
                                                <h3 className="font-display font-bold text-xl uppercase tracking-wide text-foreground">{rig.name}</h3>
                                                <p className="text-sm font-medium text-foreground/50 mt-1">{rig.type}{rig.detail ? ` — ${rig.detail}` : ''}</p>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded">{rig.badge}</span>
                                            </div>
                                        </div>
                                    </StaggerItem>
                                ))}
                            </StaggerGrid>
                        </div>

                        {/* Underground UG */}
                        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-start">
                            <Reveal direction="left" className="lg:sticky top-32">
                                <div className="inline-flex p-4 rounded-full bg-surface/5 text-primary mb-6">
                                    <HardHat strokeWidth={1.5} className="w-8 h-8" />
                                </div>
                                <h2 className="text-4xl font-display font-black uppercase tracking-tight mb-4 text-foreground">{t("categories.ug.title_line1")} <br /><span className="text-foreground/30 italic">{t("categories.ug.title_accent")}</span></h2>
                                <p className="text-foreground/60 leading-relaxed font-medium mb-6">
                                    {t("categories.ug.desc")}
                                </p>
                                <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-primary">{t("categories.ug.machine_count")}</div>
                            </Reveal>

                            <StaggerGrid className="grid gap-4">
                                {ugMachines.map((rig, i) => (
                                    <StaggerItem key={i}>
                                        <div className="group bg-surface/5 p-6 border border-border hover:border-primary/50 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div>
                                                <h3 className="font-display font-bold text-xl uppercase tracking-wide text-foreground">{rig.name}</h3>
                                                <p className="text-sm font-medium text-foreground/50 mt-1">{rig.type}{rig.detail ? ` — ${rig.detail}` : ''}</p>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <span className="font-mono text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded">{rig.badge}</span>
                                            </div>
                                        </div>
                                    </StaggerItem>
                                ))}
                            </StaggerGrid>
                        </div>

                        {/* Surface DD */}
                        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 items-start">
                            <Reveal direction="left" className="lg:sticky top-32">
                                <div className="inline-flex p-4 rounded-full bg-surface/5 text-primary mb-6">
                                    <Layers strokeWidth={1.5} className="w-8 h-8" />
                                </div>
                                <h2 className="text-4xl font-display font-black uppercase tracking-tight mb-4 text-foreground">{t("categories.dd.title_line1")} <br /><span className="text-foreground/30 italic">{t("categories.dd.title_accent")}</span></h2>
                                <p className="text-foreground/60 leading-relaxed font-medium mb-6">
                                    {t("categories.dd.desc")}
                                </p>
                                <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-primary">{t("categories.dd.machine_count")}</div>
                            </Reveal>

                            <Reveal direction="up" className="bg-surface/5 border border-border p-8 relative overflow-hidden">
                                <div className="absolute right-0 top-0 text-[6rem] md:text-[10rem] font-display font-black text-surface-foreground/5 opacity-5 pointer-events-none select-none">
                                    LF230
                                </div>

                                <div className="mb-10">
                                    <h3 className="font-display text-3xl font-black uppercase text-foreground mb-4"><span dangerouslySetInnerHTML={{ __html: t("categories.dd.title_highlight") }} /></h3>
                                    <p className="text-foreground/70 font-medium leading-relaxed max-w-xl">
                                        {t("categories.dd.desc_highlight")}
                                    </p>
                                </div>

                                <StaggerGrid className="grid sm:grid-cols-2 gap-6">
                                    {ddHighlights.map((item, i) => (
                                        <StaggerItem key={i} className="flex gap-4">
                                            <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                                            <div>
                                                <div className="font-bold text-sm text-foreground uppercase tracking-wide">{item.title}</div>
                                                <div className="text-sm text-foreground/50 mt-1">{item.desc}</div>
                                            </div>
                                        </StaggerItem>
                                    ))}
                                </StaggerGrid>
                            </Reveal>
                        </div>

                    </div>
                </section>

                {/* CTA */}
                <section className="py-16 md:py-24 px-6 bg-surface text-surface-foreground grain border-t border-surface-foreground/10">
                    <Reveal direction="up" className="mx-auto max-w-4xl text-center">
                        <h2 className="text-4xl md:text-5xl font-display font-black uppercase leading-tight mb-6">
                            {t("cta.title")}
                        </h2>
                        <p className="text-lg text-surface-foreground/70 leading-relaxed max-w-2xl mx-auto mb-10">
                            {t("cta.desc")}
                        </p>
                        <a href="/contact" className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-10 py-5 text-sm font-bold tracking-widest uppercase hover:bg-primary/90 transition shadow-xl shadow-primary/20">
                            {t("cta.button")} <ArrowUpRight className="w-5 h-5" />
                        </a>
                    </Reveal>
                </section>

                <Footer />
            </div>
        </>
    );
}
